import React from "react";
import { Navigate, Outlet } from "react-router-dom";

import BottomBar from "components/control/BottomBar";
import ControlLayout from "layout/ControlLayout";
import ControlPage from "pages/ControlPage";
import ControlLogsPage from "pages/control/ControlLogsPage";
import GalleryPage from "pages/control/GalleryPage";
import { useDeviceFeatureFlags } from "providers/DeviceFeatureFlagsProvider";
import DeviceSettingsPage from "pages/control/DeviceSettingsPage";
import DeveloperModeProvider from "providers/DeveloperModeProvider";
import { LocalePreferencesProvider } from "providers/LocalePreferencesProvider";
import IAQProvider from "providers/ARC/IAQProvider";
import LightControlProvider from "providers/ARC/LightControlProvider";
import EstopProvider from "providers/ARC/EstopProvider";
import BboxOverlaySourceProvider from "providers/BboxOverlaySourceProvider";
import WebRTCProvider from "providers/WebRTCProvider";
import WebRTCDebugProvider from "providers/WebRTCDebugProvider";
import LogHistoryProvider from "providers/LogHistoryProvider";
import Providers from "providers/Providers";
import RobotControlProvider from "providers/RobotControlProvider";
import RobotProfileLivenessProvider from "providers/RobotProfileLivenessProvider";
import CameraControlProvider from "providers/inspections/CameraControlProvider";
import DeviationDetectionProvider from "providers/inspections/DeviationDetectionProvider";
import ObjectDetectionProvider from "providers/inspections/ObjectionDetectionProvider";
import TakeReadingProvider from "providers/inspections/TakeReadingProvider";
import MapProvider from "providers/navigation/MapProvider";
import MapManagerProvider from "providers/navigation/MapManagerProvider";
import MissionControlProvider from "providers/navigation/MissionProvider";
import MissionExecutionProvider from "providers/navigation/MissionExecutionProvider";
import NavigateToPoseProvider from "providers/navigation/NavigateToPoseProvider";
import NavigationProvider from "providers/navigation/NavigationProvider";

const ControlMainRoute = () => (
    <div className="flex min-h-0 flex-1 flex-col justify-between">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <ControlPage />
        </div>
        <BottomBar />
    </div>
);

const ControlSerialOutlet = () => <Outlet />;

const MissionsRoute = ({ children }) => {
    const { flags } = useDeviceFeatureFlags();
    return flags.HAS_MISSIONS ? children : <Navigate to=".." replace />;
};

const ControlLogsRoute = () => (
    <MissionsRoute>
        <ControlLogsPage />
    </MissionsRoute>
);

/**
 * Routes under `control`. Settings and logs with a selected ARC use
 * `/control/:ARCSerialNumber/settings` and `/control/:ARCSerialNumber/logs`
 * so `ARCProvider` still resolves `selectedARC` from the URL.
 */
export const controlRouteChildren = [
    { path: "settings/*", element: <DeviceSettingsPage /> },
    { path: "logs/:runId?", element: <ControlLogsRoute /> },
    { path: "gallery", element: <GalleryPage /> },
    {
        path: ":ARCSerialNumber",
        element: <ControlSerialOutlet />,
        children: [
            { index: true, element: <ControlMainRoute /> },
            { path: "settings/*", element: <DeviceSettingsPage /> },
            { path: "logs/:runId?", element: <ControlLogsRoute /> },
            { path: "gallery", element: <GalleryPage /> },
        ],
    },
    { index: true, element: <ControlMainRoute /> },
];

export const controlRoutes = {
    path: "control",
    element: (
        <Providers
            key="Control-provider"
            providers={[
                LocalePreferencesProvider,
                DeveloperModeProvider,
                RobotControlProvider,
                NavigationProvider,
                MapProvider,
                MapManagerProvider,
                MissionControlProvider,
                MissionExecutionProvider,
                NavigateToPoseProvider,
                LogHistoryProvider,
                BboxOverlaySourceProvider,
                WebRTCProvider,
                WebRTCDebugProvider,
                LightControlProvider,
                EstopProvider,
                RobotProfileLivenessProvider,
                IAQProvider,
                CameraControlProvider,
                ObjectDetectionProvider,
                DeviationDetectionProvider,
                TakeReadingProvider,
            ]}
        >
            <ControlLayout />
        </Providers>
    ),
    children: controlRouteChildren,
};
